import { appServiceLines, caseUtils, codeBenchActions } from '@codametrix/ui-common';
import { request } from '@codametrix/client-api';
import { gql } from '@apollo/client';
import type {
  CodingScreenApi,
  HoldPayload,
  TransferRecipient
} from '../../project_starburst';
import { holdCaseArgsFromPayload } from './apiMappers/holdCaseArgs';
import {
  narrowByTerm,
  toTransferRecipients
} from './apiMappers/transferRecipients';

/**
 * the coder directory behind the Transfer modal's type-ahead.
 *
 * A DASHBOARD-OWNED document deliberately: the equivalent query lives in `packages/service-desk`,
 * which dashboard-ui does not depend on and `project_starburst` may not import. Only the transport
 * (`getServiceDeskClient`) is shared, exactly as `actions/manage-coders.action.ts` does it.
 */
const GET_ALL_CODERS = gql`
  query Coders {
    Coders {
      data
      total
    }
  }
`;

/**
 * Reconstruct the hold endpoint's `ProcessInstance{serviceLine, guid.uuid}` from the fetched coding
 * case. `state.codeBench.processInstance` is NOT reliably populated on the `/project_starburst/`
 * route (only amplify case-load actions write it), so read the authoritative values off the primary
 * `CaseRun` — the same `process_instance_guid` + resolved `amplifyPath` the app's own amplify fetch
 * uses (`getDetail` → `amplifyProcessInstance`). Returns undefined until the case has loaded, or if
 * the service line can't be resolved — and NEVER throws (a throw here would crash the shell render).
 */
export function processInstanceFromCase(
  activeCase: CMxAPI.Case | undefined
): AmplifyAPI.ProcessInstance | undefined {
  if (!activeCase?.caseRuns?.length) return undefined;
  try {
    const caseRun = caseUtils.getCaseRun(activeCase);
    const uuid = caseRun?.process_instance_guid;
    if (!uuid) return undefined;
    const slConfig = appServiceLines.determineServiceLineConfig({
      name: caseRun.service_line,
      caseIdentifier: String(caseRun.case_id ?? '')
    });
    if (!slConfig?.amplifyPath) return undefined;
    return {
      serviceLine: slConfig.amplifyPath,
      guid: { uuid }
    } as AmplifyAPI.ProcessInstance;
  } catch {
    return undefined;
  }
}

/**
 * Build the `CodingScreenApi` the coding shell injects. ONLY `placeHold` is wired to the real backend
 * (direct `PUT /coding-api/.../hold/v1` via client-api's `request`, which throws on non-2xx so a failed
 * hold actually surfaces); the rest are resolving stubs: `loadNextCase` needs the worklist cursor
 * context this route lacks, and the "Logged as" current-user has no redux source yet — both PT-1 on CDMX-18412.
 *
 * Returns undefined until the case + its ProcessInstance are available, during which `HoldAction`
 * shows "Holds are not available yet." (its own no-api guard).
 */
export function codingScreenApiFromCase(
  activeCase: CMxAPI.Case | undefined,
  dispatch: (action: unknown) => unknown
): CodingScreenApi | undefined {
  const processInstance = processInstanceFromCase(activeCase);
  if (!processInstance) return undefined;
  return {
    placeHold: async (payload: HoldPayload): Promise<void> => {
      // Issue the hold PUT DIRECTLY via client-api's `request`, which THROWS on any non-2xx
      // (ServerError/BadRequestError/UnauthenticatedError/…) -> this promise rejects -> HoldAction's
      // placeHold `catch` shows the error banner. We deliberately do NOT go through ui-common's
      // `holdCase` thunk: it catches the request error, fires a legacy toast, and still resolves
      // `fulfilled`, so its `.unwrap()` never rejects and a FAILED hold would present as success.
      const args = holdCaseArgsFromPayload(payload, processInstance);
      await request(
        `/coding-api/processinstance/${processInstance.serviceLine}/${processInstance.guid.uuid}/hold/v1`,
        {
          cache: 'no-cache',
          redirect: 'follow',
          referrer: 'no-referrer',
          method: 'PUT',
          body: JSON.stringify({
            hold_reason: args.holdReason,
            comment: args.comment,
            coding_manager_id: args.coding_manager_id ?? null
          })
        }
      );
      // Refresh the case's hold status into redux (the side effect the `holdCase` thunk used to
      // provide; PT-1 will consume it to reflect the now-on-hold state). Fire-and-forget: the hold
      // already persisted above, so a status-refresh failure must never fail the placeHold.
      dispatch(codeBenchActions.getHoldCaseForProcessInstance(processInstance));
    },
    loadNextCase: async (): Promise<void> => {},
    loadCase: async (): Promise<void> => {},
    startReview: (): void => {},
    onCodeSearch: async (): Promise<void> => {},
    /**
     * the READ half of Transfer, wired for real: it needs no new backend (the coder
     * directory already exists) and it is what makes the type-ahead demonstrable end to end.
     *
     * The WRITE half (`transferCase`) is deliberately NOT provided. No backend can persist the
     * transfer reasons, note or case context, and no transfer audit record exists (ticket
     * AC11/AC12) — so the capability stays absent and `TransferModal` renders its preview notice
     * with a disabled primary action rather than moving a case while silently discarding what the
     * coder typed. Supplying a `transferCase` here is the one-line change that turns the feature on
     * once a real endpoint exists.
     *
     * Never throws: a directory failure degrades the type-ahead to "No matches", it must not take
     * down the modal.
     */
    searchTransferRecipients: async (
      term: string
    ): Promise<readonly TransferRecipient[]> => {
      try {
        // Imported LAZILY, inside the call. `clients/service-desk.client` pulls in
        // `actions/feedback.actions` at module scope, which destructures `commonActions` from
        // `@codametrix/ui-common` -- under this app's existing partial ui-common test mocks that
        // destructure throws at import time and takes down whole suites. A dynamic import keeps the
        // dependency where it is actually used and off every consumer's module-init path.
        const { getServiceDeskClient } = await import(
          '../../clients/service-desk.client'
        );
        const client = getServiceDeskClient();
        await client.refreshToken();
        const response = await client.apolloClient.query({
          query: GET_ALL_CODERS,
          fetchPolicy: 'network-only'
        });
        // `Coders` returns either `{data: {data: rows[], total}}` or `{data: rows[]}`.
        const payload = response?.data?.Coders?.data;
        const rows = Array.isArray(payload) ? payload : (payload?.data ?? []);
        return narrowByTerm(toTransferRecipients(rows), term);
      } catch {
        return [];
      }
    }
  };
}
